/**
 * TKP-72 — Varlıklar veri kartının envanter özellikleri paneli. Panel düğümün (süreç×veri) kendi özelliklerini ve
 * ilgili kişi satırlarının özelliklerini bir arada gösterir; ilgili kişi seviyesindeki eklemeler seçilen satırlara yazılır.
 * Buradaki yardımcılar saf: store ve bileşenden bağımsız, birim testleriyle sınanır.
 */

/** Satır listesi + ilgili kişi envanteri → bölüm başına `{ row, item }` düz listesi (ilgili kişi adıyla gruplanır). */
export const flattenDatasubjectItems = (rows = [], datasubjectInventory = {}, key) =>
  (rows ?? []).flatMap((row) => ((datasubjectInventory?.[row._id] ?? {})[key] ?? []).map((item) => ({ row, item })));

/** İlgili kişi satırlarının otomatik/yapay zekâ işaretleri (satır kaydının kendi `feature` alanı). */
export const processingFeaturesOf = (rows = []) =>
  (rows ?? []).map((row) => ({ row, autoProcessing: !!row.feature?.autoProcessing, aiProcessing: !!row.feature?.aiProcessing }));

/** İşleme amacı/hukuki sebep yazımı: hazırlanan çiftler seçilen HER satıra ayrı kayıt olur. */
export const processingLegalWrites = (rowIds = [], items = []) =>
  (rowIds ?? [])
    .filter(Boolean)
    .map((rowId) => ({ datasubject: { _id: rowId }, items: (items ?? []).map((i) => ({ ...i, datasubject: { _id: rowId } })) }))
    .filter((w) => w.items.length > 0);

/** Aktarım yazımı: seçilen her satır × seçilen her 3. taraf bir kayıt (formun kendi davranışıyla aynı). */
export const transferingWrites = (rowIds = [], prepared = {}) => {
  const thirdParties = Array.isArray(prepared.thirdParty) ? prepared.thirdParty : prepared.thirdParty ? [prepared.thirdParty] : [];
  // TKP-196: üçüncü taraf isteğe bağlı — yoksa üçüncü tarafsız tek kayıt (satır başına)
  const list = thirdParties.length ? thirdParties : [undefined];
  return (rowIds ?? [])
    .filter(Boolean)
    .flatMap((rowId) => list.map((thirdParty) => ({ item: { ...prepared, datasubject: { _id: rowId }, thirdParty } })));
};

/** Toplu güncelleme sekmesinin kapsamı: düğümün birim + süreç + veri kriterleri. */
export const nodeScopeCommits = ({ department, process, node } = {}) => {
  const ref = (x) => (x && x._id ? { _id: x._id, name: x.name } : null);
  const commits = [
    ["department", ref(department)],
    ["process", ref(process)],
    ["data", ref(node && node.data)],
  ];
  if (commits.some(([, v]) => !v)) throw new Error("incomplete node scope");
  return commits;
};

/**
 * TKP-72 (ek) — kaynağa bağlı özellikler (K5: toplama, saklama, arşiv) kaydı süreç×kaynak seviyesindedir. Veri kartı paneli
 * bunları düğümün kaynakları (`datasources[]`, K1) üzerinden gösterir: liste kaynak adıyla gruplanır, ekleme/silme o kaynağın
 * kaydına gider — yani aynı kaynağı kullanan diğer verileri de etkiler (panelde uyarı).
 */
export const flattenDatasourceItems = (datasources = [], datasourceInventory = {}, key) =>
  (datasources ?? []).flatMap((datasource) => ((datasourceInventory?.[datasource._id] ?? {})[key] ?? []).map((item) => ({ datasource, item })));

/**
 * TKP-74 — kayıt iki seviyeden birinde durur. Panel listesi ikisinin birleşimi: düğümün KENDİ kayıtları ("Bu veri") ve
 * düğümün kaynaklarının kayıtları (kaynak adıyla). Veri kartından eklenen kayıt her zaman düğüme yazılır.
 */
export const featureEntries = (node, nodeInventory, datasourceInventory, key) => [
  ...(((nodeInventory || {}).own || {})[key] ?? []).map((item) => ({ item, scope: "node" })),
  ...flattenDatasourceItems((node || {}).datasources, datasourceInventory, key).map((e) => ({ item: e.item, scope: "datasource", datasource: e.datasource })),
];

/**
 * TKP-76 — panel iki yerden açılır: veri kartı (düğümün tüm ilgili kişileri) ve İLGİLİ KİŞİ kartı (yalnız o satır).
 * İlgili kişi kartından açıldığında satır listesi tek satıra iner; kayıt yazımı o satıra gider, seçim adımı gerekmez.
 */
export const panelRows = (allRows = [], focusedRow) => {
  if (!focusedRow) return allRows ?? [];
  const match = (allRows ?? []).find((r) => String(r._id) === String(focusedRow._id));
  return [match ?? focusedRow];
};

/** Toplu güncelleme kapsamı: ilgili kişi kartından açıldıysa kapsama ilgili kişi de girer. */
export const panelScope = (base = [], focusedRow) => {
  const ref = focusedRow && focusedRow.datasubject && focusedRow.datasubject._id ? { _id: focusedRow.datasubject._id, name: focusedRow.datasubject.name } : null;
  return ref ? [...base, ["datasubject", ref]] : base;
};
